import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL, NODE_API_BASE_URL } from "../../config";
import axios from "axios";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Select from "react-select";
import TableLoader from "../../Loader/TableLoader";
import { Button, Input, Tooltip, Modal } from "antd";
import {
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  AppstoreOutlined,
  TagsOutlined,
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  StopOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ShoppingOutlined,
  BarsOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { fetchUserList, updateUserStatus } from "../services/userProfileService";
import usePermissions from "../hooks/usePermissions";
import { withCountryCode } from "../utils/phones";
import { authHeaders,getSessionToken } from "../utils/session";

const LIMIT = 10;

/**
 * Subscription Type column — one chip, never two.
 *
 * `customer_type` alone only says trial vs paid, so a payment-pending customer
 * used to read as plain "Paid". `customer_status_label` carries the real state
 * ("Payment Pending", "Trial Expired", …) so it wins whenever present; the
 * trial/paid wording is only the fallback when the API sends no label.
 */
const STATUS_CHIP_COLORS = {
  payment_pending:    { background: "#fff7e6", color: "#d97706" },
  payment_rejected:   { background: "#fef2f2", color: "#dc2626" },
  paid_expired:       { background: "#fef2f2", color: "#dc2626" },
  trial_expired:      { background: "#f5f3ff", color: "#7c3aed" },
  free_trial_expired: { background: "#f5f3ff", color: "#7c3aed" },
  expiring_soon:      { background: "#fff7ed", color: "#ea580c" },
  paid_active:        { background: "#f0fdf4", color: "#16a34a" },
  paid:               { background: "#f0fdf4", color: "#16a34a" },
  active:             { background: "#f0fdf4", color: "#16a34a" },
  trial:              { background: "#e0f2fe", color: "#0369a1" },
  trial_active:       { background: "#e0f2fe", color: "#0369a1" },
  free_trial:         { background: "#e0f2fe", color: "#0369a1" },
};

const normalizeStatusKey = (value) =>
  (value || "").toString().toLowerCase().replace(/[\s-]+/g, "_");

/** Single label for the column: API status label first, trial/paid as fallback. */
const subscriptionTypeLabel = (item) => {
  if (item.customer_status_label) return item.customer_status_label;
  if (!item.customer_type) return "";
  return item.customer_type === "trial" ? "Free Trial" : "Paid";
};

const subscriptionTypeChipStyle = (item) =>
  STATUS_CHIP_COLORS[normalizeStatusKey(item.customer_status)] ||
  STATUS_CHIP_COLORS[normalizeStatusKey(item.customer_status_label)] ||
  STATUS_CHIP_COLORS[normalizeStatusKey(item.customer_type)] ||
  { background: "#f3f4f6", color: "#4b5563" };

// ── Custom DatePicker Input ────────────────────────────────────────────────────
const CustomDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      onClick={onClick}
      ref={ref}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        cursor: "pointer",
        minHeight: "42px",
        background: "white",
        transition: "all 0.2s",
        width: "100%",
        fontSize: "14px",
        color: value ? "#262626" : "#bfbfbf",
      }}
    >
      <CalendarOutlined
        style={{ color: "#667eea", fontSize: "14px", flexShrink: 0 }}
      />
      <span style={{ flex: 1 }}>{value || placeholder}</span>
    </div>
  ),
);

const applyClientFilters = (data, startDate, endDate) => {
  return data.filter((item) => {

    if (startDate || endDate) {
      // Response dates are "DD/MM/YYYY"
      const parseDate = (str) => {
        if (!str) return null;
        const p = str.split("/");
        if (p.length !== 3) return null;
        return new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0]));
      };
      const itemEnd = parseDate(item.subscription_end_date);
      if (!itemEnd) return false;
      if (startDate) {
        const from = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        if (itemEnd < from) return false;
      }
      if (endDate) {
        const to = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        if (itemEnd > to) return false;
      }
    }
    return true;
  });
};

// ── Reusable styled confirm dialog ─────────────────────────────────────────────
const showConfirm = ({
  type = "danger",
  icon,
  title,
  description,
  okText,
  cancelText = "Cancel",
  onOk,
}) => {
  const isDanger = type === "danger";
  const isWarning = type === "warning";
  const iconColor = isDanger ? "#ff4d4f" : isWarning ? "#faad14" : "#1677ff";
  const iconBg = isDanger ? "#fff2f0" : isWarning ? "#fffbe6" : "#e6f4ff";
  const okBtnColor = isDanger ? "#ff4d4f" : isWarning ? "#faad14" : "#1677ff";

  Modal.confirm({
    icon: null,
    title: null,
    content: (
      <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            fontSize: 28,
            color: iconColor,
            border: `2px solid ${iconColor}22`,
          }}
        >
          {icon}
        </div>
        <div
          style={{
            fontWeight: 700,
            fontSize: 17,
            color: "#1f2937",
            marginBottom: 8,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
          {description}
        </div>
      </div>
    ),
    okText,
    cancelText,
    centered: true,
    okButtonProps: {
      style: {
        background: okBtnColor,
        borderColor: okBtnColor,
        borderRadius: 8,
        fontWeight: 600,
        minWidth: 90,
      },
      danger: isDanger,
    },
    cancelButtonProps: {
      style: { borderRadius: 8, fontWeight: 600, minWidth: 90 },
    },
    onOk,
  });
};

const ManageUserData = () => {
  const { canCreate, canDelete } = usePermissions("/cms/manage-customer");
  const [viewMode, setViewMode] = useState("list");
  const [kanbanFilter, setKanbanFilter] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const { search, state: locationState } = useLocation();
  const navigate = useNavigate();
  const highlightUserId = new URLSearchParams(search).get("userId");
  const highlightUserName = new URLSearchParams(search).get("userName");

  const [listPageData, setListPageData] = useState([]);
  const [kanbanAllData, setKanbanAllData] = useState([]);
  const [isKanbanLoading, setIsKanbanLoading] = useState(false);
  const [exportMode, setExportMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [token, setToken] = useState(getSessionToken());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageApi, setPageApi] = useState(1);
  const [totalUser, setTotalUser] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [plans, setPlans] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimerRef = useRef(null);

  const [filterData, setFilterData] = useState({
    plan_id: "",
    is_attorney: locationState?.is_attorney != null ? locationState.is_attorney : "",
    subscription_type: locationState?.subscription_type ?? "",
    plan_type: locationState?.plan_type ?? "",
    /* Window on the FIRST order's creation date — this is what the dashboard's
       "New This Week" card counts, so following that card lands on exactly those
       customers and the two numbers always agree. */
    subscription_created_from: locationState?.subscription_created_from ?? "",
    subscription_created_to: locationState?.subscription_created_to ?? "",
  });

  // ── Scroll refs ──
  const scrollContainerRef = useRef(null);
  const throttleRef = useRef(false);
  const pageRef = useRef(1);
  const filterDataRef = useRef(null);
  const fetchPageRef = useRef(null);
  // Always keep refs current so memoized loadMore never has stale filters/fetchPage
  filterDataRef.current = filterData;

  // Skip the generic debouncedSearch-triggered fetch on first mount when dashboard
  // pre-sets a filter — the explicit mount effect below handles that case instead
  const hasInitialFilter = locationState?.is_attorney != null || !!locationState?.subscription_type || !!locationState?.plan_type || !!locationState?.plan_name || !!locationState?.subscription_created_from;
  const skipNextSearchFetchRef = useRef(hasInitialFilter);

  // ── Debounce search ──
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchQuery]);

  // ── Re-fetch when debounced search changes ──
  useEffect(() => {
    if (skipNextSearchFetchRef.current) {
      skipNextSearchFetchRef.current = false;
      return;
    }
    resetAndFetch();
  }, [debouncedSearch]);

  // ── Initial load ──
  useEffect(() => {
    setToken(getSessionToken());
    loadplan2();
    // If coming from dashboard with pre-set filter, trigger fetch exactly like the dropdown does
    if (hasInitialFilter) {
      const initFilters = {
        plan_id: "",
        is_attorney: locationState?.is_attorney != null ? locationState.is_attorney : "",
        subscription_type: locationState?.subscription_type ?? "",
        plan_type: locationState?.plan_type ?? "",
        subscription_created_from: locationState?.subscription_created_from ?? "",
        subscription_created_to: locationState?.subscription_created_to ?? "",
      };
      if (!locationState?.plan_name) {
        setFilterData(initFilters);
        resetAndFetch(initFilters);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle highlightUserId changes ──
  useEffect(() => {
    if (highlightUserId) {
      setSearchQuery("");
      setDebouncedSearch("");
      const freshFilters = { plan_id: "", is_attorney: "", subscription_type: "", plan_type: "", subscription_created_from: "", subscription_created_to: "" };
      setFilterData(freshFilters);
      resetAndFetch(freshFilters);
    } else if (!hasInitialFilter) {
      setSearchQuery("");
      setDebouncedSearch("");
      const clearedFilters = { plan_id: "", is_attorney: "", subscription_type: "", plan_type: "", subscription_created_from: "", subscription_created_to: "" };
      setFilterData(clearedFilters);
      setStartDate(null);
      setEndDate(null);
      resetAndFetch(clearedFilters);
    }
  }, [highlightUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ══════════════════════════════════════════════════════════════════
  // FETCH + INFINITE SCROLL LOGIC
  // ══════════════════════════════════════════════════════════════════

  const fetchPage = async (page, filters = filterData, isReset = false) => {
    if (isReset) setIsLoading(true);
    else setIsLoadingMore(true);

    try {
      const finalFilters = {};
      if (debouncedSearch) finalFilters.search = debouncedSearch;
      if (filters.plan_id) finalFilters.plan_id = filters.plan_id;
      if (
        filters.is_attorney !== "" &&
        filters.is_attorney !== null &&
        filters.is_attorney !== undefined
      ) {
        finalFilters.is_attorney = filters.is_attorney;
      }
      if (filters.subscription_type === "free_trial") finalFilters.customer_type = "trial";
      else if (filters.subscription_type === "paid") finalFilters.customer_status = "paid_active";
      if (filters.plan_type) finalFilters.plan_type = filters.plan_type;
      if (filters.subscription_created_from)
        finalFilters.subscription_created_from = filters.subscription_created_from;
      if (filters.subscription_created_to)
        finalFilters.subscription_created_to = filters.subscription_created_to;

      const hasClientFilters = !!startDate || !!endDate;
      const effectiveLimit = hasClientFilters ? 1000 : LIMIT;

      const currentUserId = highlightUserId;

      // When filtering by userId, fetch all and filter client-side
      if (currentUserId) {
        const response = await fetchUserList(1, {});
        const allData = response?.data || [];
        const filtered = allData.filter(
          (item) => String(item.user_id) === String(currentUserId),
        );
        setListPageData(filtered);
        setTotalUser(filtered.length);
        setHasMore(false);
        return;
      }

      const response = await fetchUserList(page, { ...finalFilters, limit: effectiveLimit });
      const rawData = response?.data || [];
      const total = response?.total || 0;
      const newData = applyClientFilters(rawData, startDate, endDate);

      setTotalUser(hasClientFilters ? newData.length : total);

      if (isReset) {
        setListPageData(newData);
      } else {
        setListPageData((prev) => [...prev, ...newData]);
      }

      const totalFetched = isReset
        ? rawData.length
        : (page - 1) * effectiveLimit + rawData.length;
      if (rawData.length < effectiveLimit || totalFetched >= total) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  fetchPageRef.current = fetchPage;

  const resetAndFetch = (overrideFilters) => {
    setListPageData([]);
    setPageApi(1);
    pageRef.current = 1;
    setHasMore(true);
    fetchPage(1, overrideFilters || filterData, true);
  };

  const fetchKanbanAll = async (filters = filterData) => {
    setIsKanbanLoading(true);
    try {
      const finalFilters = {};
      if (debouncedSearch) finalFilters.search = debouncedSearch;
      if (filters.plan_id) finalFilters.plan_id = filters.plan_id;
      if (
        filters.is_attorney !== "" &&
        filters.is_attorney !== null &&
        filters.is_attorney !== undefined
      )
        finalFilters.is_attorney = filters.is_attorney;
      if (filters.subscription_type === "free_trial") finalFilters.customer_type = "trial";
      else if (filters.subscription_type === "paid") finalFilters.customer_status = "paid_active";
      if (filters.plan_type) finalFilters.plan_type = filters.plan_type;
      if (filters.subscription_created_from)
        finalFilters.subscription_created_from = filters.subscription_created_from;
      if (filters.subscription_created_to)
        finalFilters.subscription_created_to = filters.subscription_created_to;

      const response = await axios.get(`${NODE_API_BASE_URL}/api/subscription/get-customer-list-v3`, {
        params: { page: 1, limit: 1000, ...finalFilters },
        headers: { ...authHeaders() },
      });
      const raw = response?.data?.data || [];
      const data = applyClientFilters(Array.isArray(raw) ? raw : [], startDate, endDate);
      setKanbanAllData(data);
    } catch (e) {
      console.error("Kanban fetch error:", e);
    } finally {
      setIsKanbanLoading(false);
    }
  };

  // Fetch all data for kanban when kanban view is active or filters change
  useEffect(() => {
    if (viewMode === "kanban") {
      fetchKanbanAll();
    }
  }, [viewMode, debouncedSearch, filterData, startDate, endDate]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    setPageApi(nextPage);
    fetchPageRef.current(nextPage, filterDataRef.current);
  }, [isLoadingMore, hasMore, isLoading]);

  const handleScroll = useCallback(
    (e) => {
      if (throttleRef.current || !hasMore || isLoadingMore) return;
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
        throttleRef.current = true;
        loadMore();
        setTimeout(() => {
          throttleRef.current = false;
        }, 300);
      }
    },
    [loadMore, hasMore, isLoadingMore],
  );

  // Attach scroll listener to list view container
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || viewMode !== "list") return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [viewMode, handleScroll]);

  // ══════════════════════════════════════════════════════════════════

  const loadplan2 = async () => {
    try {
      const plan_data = await axios.get(API_BASE_URL + "/get-all-plans-v1",
         {headers: { ...authHeaders() }}
      );
      if (plan_data.data?.status === "success") {
        const allPlans = plan_data.data.data;
        setPlans(allPlans);
        if (locationState?.plan_name) {
          const match = allPlans.find((p) => p.name === locationState.plan_name);
          if (match) {
            const initFilters = { plan_id: match.id, is_attorney: "", subscription_type: "", plan_type: "" };
            setFilterData(initFilters);
            resetAndFetch(initFilters);
          }
        }
      }
    } catch (e) {
      return false;
    }
  };

  const ClearFilter = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setKanbanFilter(null);
    setFilterData({ plan_id: "", is_attorney: "", subscription_type: "", plan_type: "" });
    setSearchQuery("");
    setDebouncedSearch("");
    setStartDate(null);
    setEndDate(null);
    resetAndFetch({ plan_id: "", is_attorney: "", subscription_type: "", plan_type: "" });
  };

  const toggleExportMode = () => {
    setExportMode((prev) => !prev);
    setSelectedIds([]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === displayData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(displayData.map((u) => u.user_id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleExportCSV = () => {
    const toExport =
      selectedIds.length > 0
        ? displayData.filter((u) => selectedIds.includes(u.user_id))
        : displayData;

    const headers = [
      "Name",
      "Email",
      "Mobile",
      "User Type",
      "Plan",
      "Plan Type",
      "Total TM Count",
      "Subscription Type",
      "Subscription Start",
      "Subscription End",
      "Status",
    ];

    const rows = toExport.map((u) => [
      u.customer_name || "",
      u.customer_email || "",
      u.mobile || "",
      u.is_attorney === 1 ? "Attorney/IP" : "Corporate",
      u.plan_name || "",
      u.latest_order_plan_type || "",
      // Same rule as the table: blank means the API did not say, not zero.
      u.total_tm_count ?? "",
      // Match the on-screen column: status label first, trial/paid as fallback
      subscriptionTypeLabel(u),
      u.subscription_start_date || "",
      u.subscription_end_date || "",
      u.is_active === 1 ? "Active" : "Inactive",
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customers_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    setExportMode(false);
    setSelectedIds([]);
  };

  const subscription_type_Options = [
    { value: "free_trial", label: "Free Trail" },
    { value: "paid", label: "Paid" },
  ];

  const attorneyOptions = [
    { value: 0, label: "Corporates" },
    { value: 1, label: "Attorney / IP Professional" },
  ];

  const handleDelete = (id) => {
    showConfirm({
      type: "danger",
      icon: <DeleteOutlined />,
      title: "Delete User",
      description:
        "Are you sure you want to delete this user? This action is permanent and cannot be undone.",
      okText: "Yes, Delete",
      onOk: () => {
        axios
          .post(`${API_BASE_URL}/delete-subscribed-user`, 
            {
              user_id: String(id),
          }, {
            headers: { ...authHeaders() },
          })
          .then((response) => {
            if (response.data.status === 200) {
              toast.success("User deleted successfully");
              resetAndFetch();
            }
          });
      },
    });
  };

  const handleActive = (status, id) => {
    const isCurrentlyActive = status === 1;
    showConfirm({
      type: isCurrentlyActive ? "danger" : "warning",
      icon: isCurrentlyActive ? <StopOutlined /> : <CheckCircleOutlined />,
      title: isCurrentlyActive ? "Deactivate User" : "Activate User",
      description: isCurrentlyActive
        ? "Are you sure you want to deactivate this user? They will lose access to their account immediately."
        : "Are you sure you want to activate this user? They will regain full access to their account.",
      okText: isCurrentlyActive ? "Yes, Deactivate" : "Yes, Activate",
      onOk: async () => {
        const updatedStatus = isCurrentlyActive ? 0 : 1;
        try {
          await updateUserStatus({ userId: id, status: updatedStatus });
          resetAndFetch();
          toast.success(
            isCurrentlyActive
              ? "User deactivated successfully"
              : "User activated successfully",
          );
        } catch (err) {
          toast.error(err.message || "Failed to update status");
        }
      },
    });
  };

  const selectStyles = () => ({
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#667eea" : "#e0e0e0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(102,126,234,0.15)" : "none",
      "&:hover": { borderColor: "#667eea" },
      minHeight: "42px",
      borderRadius: "8px",
      transition: "all 0.2s",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#667eea"
        : state.isFocused
          ? "#f0f4ff"
          : "white",
      color: state.isSelected ? "white" : "#262626",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    }),
  });

  const planOption = [];
  plans?.forEach((item) => {
    planOption.push({ value: item.id, label: item.name });
  });

  const planTypeOptions = [
    { value: "free_trial", label: "Free Trial" },
    { value: "annually", label: "Annually" },
    { value: "monthly", label: "Monthly" },
  ];

  const activeFiltersCount = [
    filterData.plan_id,
    filterData.is_attorney !== "" && filterData.is_attorney !== null ? filterData.is_attorney : null,
    filterData.subscription_type,
    filterData.plan_type,
    startDate,
    endDate,
  ].filter((v) => v !== "" && v !== null && v !== undefined).length;

  const labelStyle = {
    fontWeight: 600,
    color: "#374151",
    fontSize: "13px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  };

  const displayData = kanbanFilter
    ? (listPageData || []).filter((u) => {
        if (kanbanFilter === "attorney") return u.is_attorney === 1;
        if (kanbanFilter === "corporate") return u.is_attorney === 0;
        return true;
      })
    : listPageData;

  // ── Loading more indicator ──
  const LoadingMoreRow = () => (
    <div style={{ textAlign: "center", padding: "16px", color: "#9ca3af" }}>
      <div
        className="spinner-border spinner-border-sm"
        style={{ color: "#667eea", marginRight: 8 }}
        role="status"
      />
      <span style={{ fontSize: 13 }}>Loading more...</span>
    </div>
  );

  return (
    <>
      {/* ── DatePicker Global Styles ── */}
      <style>{`
        .custom-datepicker-wrapper { width: 100%; }
        .custom-datepicker-wrapper .react-datepicker-wrapper { width: 100%; }
        .custom-datepicker-wrapper .react-datepicker__input-container { width: 100%; }
        .custom-datepicker-wrapper .react-datepicker-popper { z-index: 9999 !important; }
        .custom-datepicker-wrapper .react-datepicker {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          border: 1px solid #e8ecff; border-radius: 12px;
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15); overflow: hidden; font-size: 13px;
        }
        .custom-datepicker-wrapper .react-datepicker__month-container { width: 280px; }
        .custom-datepicker-wrapper .react-datepicker__header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-bottom: none; padding: 10px 8px 6px; border-radius: 0;
        }
        .custom-datepicker-wrapper .react-datepicker__current-month { color: white; font-weight: 700; font-size: 14px; margin-bottom: 4px; }
        .custom-datepicker-wrapper .react-datepicker__day-names { display: flex; justify-content: space-around; }
        .custom-datepicker-wrapper .react-datepicker__day-name { color: rgba(255,255,255,0.85); font-weight: 600; font-size: 11px; width: 32px; line-height: 32px; margin: 1px; text-align: center; }
        .custom-datepicker-wrapper .react-datepicker__month { margin: 6px; }
        .custom-datepicker-wrapper .react-datepicker__week { display: flex; justify-content: space-around; }
        .custom-datepicker-wrapper .react-datepicker__day { width: 32px; line-height: 32px; margin: 1px; border-radius: 6px; font-size: 13px; color: #374151; transition: all 0.15s; text-align: center; }
        .custom-datepicker-wrapper .react-datepicker__day:hover { background: #f0f4ff; color: #667eea; }
        .custom-datepicker-wrapper .react-datepicker__day--selected,
        .custom-datepicker-wrapper .react-datepicker__day--keyboard-selected { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; color: white !important; font-weight: 600; border-radius: 6px; }
        .custom-datepicker-wrapper .react-datepicker__day--today { font-weight: 700; color: #667eea; }
        .custom-datepicker-wrapper .react-datepicker__day--disabled { color: #d1d5db !important; }
        .custom-datepicker-wrapper .react-datepicker__day--outside-month { color: #d1d5db; }
        .custom-datepicker-wrapper .react-datepicker__navigation { top: 10px; }
        .custom-datepicker-wrapper .react-datepicker__navigation-icon::before { border-color: rgba(255,255,255,0.8); border-width: 2px 2px 0 0; height: 7px; width: 7px; }
        .custom-datepicker-wrapper .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before { border-color: white; }
        .custom-datepicker-wrapper .react-datepicker__triangle { display: none; }
      `}</style>

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* ── PAGE HEADER ── */}
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "16px",
                padding: "28px 32px",
                marginBottom: "28px",
                boxShadow: "0 8px 32px rgba(102,126,234,0.28)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    background: "rgba(255,255,255,0.22)",
                    borderRadius: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "26px",
                    color: "white",
                  }}
                >
                  <TeamOutlined />
                </div>
                <div>
                  <h4
                    style={{
                      color: "white",
                      margin: 0,
                      fontSize: "24px",
                      fontWeight: 700,
                    }}
                  >
                    Manage User Data
                  </h4>
                  <p
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      margin: 0,
                      fontSize: "14px",
                    }}
                  >
                    {highlightUserName
                      ? `Showing details for ${decodeURIComponent(highlightUserName)}`
                      : "View and manage all customer records"}
                  </p>
                </div>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.18)",
                  padding: "10px 18px",
                  borderRadius: "10px",
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  fontSize: "13px",
                  color: "white",
                }}
              >
                <a
                  href="javascript:void(0);"
                  style={{ color: "white", textDecoration: "none" }}
                >
                  CMS
                </a>
                <span style={{ color: "rgba(255,255,255,0.5)" }}>›</span>
                <span style={{ fontWeight: 600 }}>Manage User Data</span>
              </div>
            </div>

            {/* ── FILTER CARD ── */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                border: "1px solid #f0f0f0",
                marginBottom: "24px",
                overflow: "visible",
              }}
            >
              {/* ── Always-visible search row ── */}
              <div style={{ padding: "16px 24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <Input
                  size="large"
                  placeholder="Search by name, email or mobile..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (!e.target.value) {
                      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                      setDebouncedSearch("");
                    }
                  }}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  style={{ borderRadius: "8px", flex: 1 }}
                  allowClear
                />
                <Button
                  size="large"
                  icon={<FilterOutlined />}
                  onClick={() => setShowAdvancedFilters(p => !p)}
                  style={{
                    borderRadius: "10px",
                    fontWeight: 600,
                    height: "44px",
                    borderColor: showAdvancedFilters ? "#667eea" : "#e0e0e0",
                    color: showAdvancedFilters ? "#667eea" : "#6b7280",
                    background: showAdvancedFilters ? "#f0f4ff" : "white",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  Advanced Filters
                  {activeFiltersCount > 0 && (
                    <span style={{ background: "linear-gradient(135deg,#667eea,#764ba2)", color: "white", borderRadius: 20, padding: "1px 8px", fontSize: 12, fontWeight: 600, marginLeft: 2 }}>
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  onClick={() => resetAndFetch()}
                  style={{ borderRadius: "10px", fontWeight: 600, height: "44px", background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", border: "none", boxShadow: "0 4px 12px rgba(102,126,234,0.4)" }}
                >
                  Search
                </Button>
              </div>

              {/* ── Collapsible advanced filters ── */}
              {showAdvancedFilters && (
                <div style={{ borderTop: "1px solid #f0f0f0", padding: "20px 24px" }}>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div style={labelStyle}><UserOutlined style={{ color: "#667eea" }} /> User Type</div>
                      <Select
                        options={attorneyOptions}
                        value={attorneyOptions.find((opt) => opt.value === filterData.is_attorney) ?? null}
                        placeholder="Select Attorney Type"
                        styles={selectStyles()}
                        isClearable
                        onChange={(selectedOption) => {
                          const updated = { ...filterData, is_attorney: selectedOption ? selectedOption.value : "" };
                          setFilterData(updated);
                          resetAndFetch(updated);
                        }}
                      />
                    </div>

                    <div className="col-md-4">
                      <div style={labelStyle}><TagsOutlined style={{ color: "#667eea" }} /> Subscription Type</div>
                      <Select
                        options={subscription_type_Options}
                        value={subscription_type_Options.find((opt) => opt.value === filterData.subscription_type) ?? null}
                        placeholder="Select Subscription"
                        styles={selectStyles()}
                        isClearable
                        onChange={(selectedOption) => {
                          const updated = { ...filterData, subscription_type: selectedOption ? selectedOption.value : "" };
                          setFilterData(updated);
                          resetAndFetch(updated);
                        }}
                      />
                    </div>

                    <div className="col-md-4">
                      <div style={labelStyle}><ShoppingOutlined style={{ color: "#667eea" }} /> Plan</div>
                      <Select
                        options={planOption}
                        value={planOption.find((opt) => opt.value === filterData.plan_id) ?? null}
                        placeholder="Select Plan"
                        styles={selectStyles()}
                        isClearable
                        onChange={(selectedOption) => {
                          const updated = { ...filterData, plan_id: selectedOption ? selectedOption.value : "" };
                          setFilterData(updated);
                          resetAndFetch(updated);
                        }}
                      />
                    </div>

                    <div className="col-md-4">
                      <div style={labelStyle}><FilterOutlined style={{ color: "#667eea" }} /> Plan Type</div>
                      <Select
                        options={planTypeOptions}
                        value={planTypeOptions.find((opt) => opt.value === filterData.plan_type) ?? null}
                        placeholder="Select Plan Type"
                        styles={selectStyles()}
                        isClearable
                        onChange={(selectedOption) => {
                          const updated = { ...filterData, plan_type: selectedOption ? selectedOption.value : "" };
                          setFilterData(updated);
                          resetAndFetch(updated);
                        }}
                      />
                    </div>

                    <div className="col-md-4">
                      <div style={labelStyle}><CalendarOutlined style={{ color: "#667eea" }} /> Subscription End Date From</div>
                      <div className="custom-datepicker-wrapper">
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => setStartDate(date)}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          dateFormat="dd MMM yyyy"
                          placeholderText="Select start date"
                          isClearable
                          customInput={<CustomDateInput placeholder="Select start date" />}
                          popperPlacement="bottom-start"
                          popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }, { name: "offset", options: { offset: [0, 4] } }]}
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div style={labelStyle}><CalendarOutlined style={{ color: "#667eea" }} /> Subscription End Date To</div>
                      <div className="custom-datepicker-wrapper">
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => setEndDate(date)}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          dateFormat="dd MMM yyyy"
                          placeholderText="Select end date"
                          isClearable
                          customInput={<CustomDateInput placeholder="Select end date" />}
                          popperPlacement="bottom-start"
                          popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }, { name: "offset", options: { offset: [0, 4] } }]}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "16px" }}>
                    <Button size="large" icon={<ClearOutlined />} onClick={ClearFilter} style={{ borderRadius: "10px", fontWeight: 600, borderColor: "#667eea", color: "#667eea", height: "44px" }}>
                      Clear Filters
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* ── TABLE / KANBAN CARD ── */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                border: "1px solid #f0f0f0",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "2px solid #f5f5f5",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "16px",
                    }}
                  >
                    <TeamOutlined />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "17px",
                        color: "#1f2937",
                      }}
                    >
                      Customer List
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {totalUser} total {totalUser === 1 ? "record" : "records"}
                    </div>
                  </div>
                  <span
                    style={{
                      background: "#f0f4ff",
                      color: "#667eea",
                      borderRadius: "20px",
                      padding: "3px 12px",
                      fontSize: "13px",
                      fontWeight: 700,
                      marginLeft: "4px",
                    }}
                  >
                    {totalUser}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  {/* ── Export Controls ── */}
                  {exportMode ? (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      {/* Select All checkbox */}
                      <Button
                        onClick={toggleSelectAll}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: "1px solid #c7d2fe",
                          background: "#f0f4ff",
                          color: "#667eea",
                          fontWeight: 600,
                          fontSize: 13,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          paddingInline: 12,
                          boxShadow: "none",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedIds.length === displayData.length &&
                            displayData.length > 0
                          }
                          onChange={toggleSelectAll}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            cursor: "pointer",
                            width: 14,
                            height: 14,
                            margin: 0,
                          }}
                        />
                        Select All ({displayData.length})
                      </Button>

                      {/* Export CSV button */}
                      <Button
                        onClick={handleExportCSV}
                        icon={<DownloadOutlined />}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: "none",
                          background:
                            "linear-gradient(135deg, #22c55e, #16a34a)",
                          color: "white",
                          fontWeight: 600,
                          fontSize: 13,
                          boxShadow: "0 4px 12px rgba(34,197,94,0.3)",
                          paddingInline: 16,
                        }}
                      >
                        Export{" "}
                        {selectedIds.length > 0
                          ? `(${selectedIds.length})`
                          : `All (${displayData.length})`}
                      </Button>

                      {/* Cancel button */}
                      <Button
                        onClick={toggleExportMode}
                        icon={<span style={{ fontSize: 12 }}>✕</span>}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: "1px solid #e2e8f0",
                          background: "white",
                          color: "#6b7280",
                          fontWeight: 600,
                          fontSize: 13,
                          paddingInline: 14,
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={toggleExportMode}
                      icon={<DownloadOutlined />}
                      style={{
                        height: 36,
                        borderRadius: 8,
                        border: "none",
                        background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: 13,
                        paddingInline: 18,
                        boxShadow: "0 4px 12px rgba(8,145,178,0.3)",
                      }}
                    >
                      Export
                    </Button>
                  )}

                  {/* View toggle */}
                  <div
                    style={{
                      display: "flex",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <button
                      onClick={() => {
                        setViewMode("kanban");
                        setKanbanFilter(null);
                      }}
                      style={{
                        padding: "7px 14px",
                        background:
                          viewMode === "kanban"
                            ? "linear-gradient(135deg,#667eea,#764ba2)"
                            : "white",
                        color: viewMode === "kanban" ? "#fff" : "#6b7280",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 13,
                        fontWeight: 600,
                        transition: "all 0.2s",
                      }}
                    >
                      <AppstoreOutlined /> Kanban
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      style={{
                        padding: "7px 14px",
                        background:
                          viewMode === "list"
                            ? "linear-gradient(135deg,#667eea,#764ba2)"
                            : "white",
                        color: viewMode === "list" ? "#fff" : "#6b7280",
                        border: "none",
                        borderLeft: "1px solid #e2e8f0",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 13,
                        fontWeight: 600,
                        transition: "all 0.2s",
                      }}
                    >
                      <BarsOutlined /> List
                    </button>
                  </div>
                  {highlightUserId && (
                    <button
                      onClick={() => navigate("/cms/manage-customer")}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "white",
                        color: "#667eea",
                        borderRadius: "10px",
                        padding: "8px 18px",
                        fontWeight: 600,
                        fontSize: "14px",
                        border: "2px solid #667eea",
                        cursor: "pointer",
                        boxShadow: "0 4px 12px rgba(102,126,234,0.3)",
                      }}
                    >
                      <span
                        style={{
                          width: "22px",
                          height: "22px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: "6px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "13px",
                        }}
                      >
                        ‹
                      </span>
                      All Customers
                    </button>
                  )}
                  {canCreate && (
                    <Link
                      to="/cms/manageUserData/addUser"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "linear-gradient(135deg, #52c41a, #389e0d)",
                        color: "white",
                        borderRadius: "10px",
                        padding: "8px 18px",
                        fontWeight: 600,
                        fontSize: "14px",
                        textDecoration: "none",
                        boxShadow: "0 4px 12px rgba(82,196,26,0.3)",
                      }}
                    >
                      <PlusOutlined /> Add Customer
                    </Link>
                  )}
                </div>
              </div>

              {/* ═══ LIST VIEW ═══ */}
              {viewMode === "list" ? (
                <>
                  {kanbanFilter && (
                    <div
                      style={{
                        padding: "10px 16px",
                        background: "#f0f4ff",
                        borderBottom: "1px solid #e8ecff",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          color: "#667eea",
                          fontWeight: 600,
                        }}
                      >
                        Showing:{" "}
                        {kanbanFilter === "attorney"
                          ? "Attorney/IP Professional"
                          : "Corporate"}{" "}
                        customers
                      </span>
                      <button
                        onClick={() => setKanbanFilter(null)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#9ca3af",
                          cursor: "pointer",
                          fontSize: 17,
                          lineHeight: 1,
                          padding: "0 4px",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div
                    ref={scrollContainerRef}
                    style={{
                      maxHeight: "calc(100vh - 340px)",
                      overflowY: "auto",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                        <tr
                          style={{
                            background:
                              "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                            borderBottom: "2px solid #e8ecff",
                          }}
                        >
                          {exportMode && (
                            <th
                              style={{
                                padding: "14px 16px",
                                width: 40,
                                background: "#f8f9ff",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  selectedIds.length === displayData.length &&
                                  displayData.length > 0
                                }
                                onChange={toggleSelectAll}
                                style={{
                                  cursor: "pointer",
                                  width: 15,
                                  height: 15,
                                }}
                              />
                            </th>
                          )}
                          {[
                            "Name",
                            "Email",
                            "Mobile",
                            /* User type first: it decides which plans and
                               allowances the rest of the row even means. */
                            "User Type",
                            "Plan",
                            "Plan Type",
                            "Total TM Count",
                            "Subscription Type",
                            "Subscription Start",
                            "Subscription End",
                            "Status",
                            "Actions",
                          ].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#667eea",
                                textTransform: "uppercase",
                                letterSpacing: "0.6px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <TableLoader count={10} />
                        ) : displayData?.length > 0 ? (
                          displayData.map((item, index) => (
                            <tr
                              key={`${item.user_id}-${index}`}
                              style={{
                                borderBottom: "1px solid #f5f5f5",
                                transition: "background 0.15s",
                                background:
                                  index % 2 === 0 ? "white" : "#fafbff",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f0f4ff";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  index % 2 === 0 ? "white" : "#fafbff";
                              }}
                            >
                              {exportMode && (
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.includes(item.user_id)}
                                    onChange={() =>
                                      toggleSelectOne(item.user_id)
                                    }
                                    style={{
                                      cursor: "pointer",
                                      width: 15,
                                      height: 15,
                                    }}
                                  />
                                </td>
                              )}
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "left",
                                }}
                              >
                                <Link
                                  to={`/cms/manageUserData/user-360-view/${item.user_id}`}
                                  state={{ userName: item.customer_name }}
                                  style={{ textDecoration: "none" }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "34px",
                                        height: "34px",
                                        borderRadius: "50%",
                                        background:
                                          "linear-gradient(135deg, #667eea, #764ba2)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "white",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {item.customer_name
                                        ?.split(" ")
                                        .map((w) => w.charAt(0))
                                        .join("")
                                        .slice(0, 2)
                                        .toUpperCase() || "?"}
                                    </div>
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#1f2937",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {item.customer_name}
                                    </span>
                                  </div>
                                </Link>
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  color: "#4b5563",
                                  fontSize: "13px",
                                  textAlign: "center",
                                }}
                              >
                                {item.customer_email}
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  color: "#4b5563",
                                  fontSize: "13px",
                                  textAlign: "center",
                                }}
                              >
                                {item.mobile || (
                                  <span
                                    style={{
                                      color: "#9ca3af",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    N/A
                                  </span>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                }}
                              >
                                <span
                                  style={{
                                    background: "#fff7e6",
                                    color: "#d97706",
                                    borderRadius: "6px",
                                    padding: "3px 10px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    display: "inline-block",
                                  }}
                                >
                                  {item.is_attorney === 1
                                    ? "Attorney/IP"
                                    : "Corporates"}
                                </span>
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                }}
                              >
                                {item.plan_name ? (
                                  <span
                                    style={{
                                      background: "#f0f4ff",
                                      color: "#667eea",
                                      borderRadius: "6px",
                                      padding: "3px 10px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      display: "inline-block",
                                    }}
                                  >
                                    {item.plan_name}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      color: "#9ca3af",
                                      fontStyle: "italic",
                                      fontSize: "13px",
                                    }}
                                  >
                                    N/A
                                  </span>
                                )}
                              </td>
                              {/* Plan Type */}
                              <td style={{ padding: "14px 16px", textAlign: "center" }}>
                                {item.latest_order_plan_type ? (
                                  <span
                                    style={{
                                      background: "#f0f4ff",
                                      color: "#667eea",
                                      borderRadius: "6px",
                                      padding: "3px 10px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      textTransform: "capitalize",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.latest_order_plan_type.replace(/_/g, " ")}
                                  </span>
                                ) : (
                                  <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "13px" }}>N/A</span>
                                )}
                              </td>
                              {/* Total TM Count — how many trademarks this
                                  customer's subscription actually covers. Blank
                                  rather than 0 when the API does not send it:
                                  "none" and "not told" are different answers. */}
                              <td style={{ padding: "14px 16px", textAlign: "center" }}>
                                {item.total_tm_count != null && item.total_tm_count !== "" ? (
                                  <span
                                    style={{
                                      background: "#ecfdf5",
                                      color: "#059669",
                                      borderRadius: "6px",
                                      padding: "3px 10px",
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    {Number(item.total_tm_count).toLocaleString("en-IN")}
                                  </span>
                                ) : (
                                  <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "13px" }}>N/A</span>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                }}
                              >
                                {(() => {
                                  const label = subscriptionTypeLabel(item);
                                  if (!label) {
                                    return (
                                      <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "13px" }}>N/A</span>
                                    );
                                  }
                                  const chip = subscriptionTypeChipStyle(item);
                                  return (
                                    <span
                                      style={{
                                        background: chip.background,
                                        color: chip.color,
                                        borderRadius: "6px",
                                        padding: "3px 10px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        display: "inline-block",
                                      }}
                                    >
                                      {label}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  color: "#4b5563",
                                  fontSize: "13px",
                                  whiteSpace: "nowrap",
                                  textAlign: "center",
                                }}
                              >
                                {item.subscription_start_date ? (
                                  moment(
                                    item.subscription_start_date,
                                    "DD/MM/YYYY",
                                  ).format("DD MMM YYYY")
                                ) : (
                                  <span
                                    style={{
                                      color: "#9ca3af",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    N/A
                                  </span>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  color: "#4b5563",
                                  fontSize: "13px",
                                  whiteSpace: "nowrap",
                                  textAlign: "center",
                                }}
                              >
                                {item.subscription_end_date ? (
                                  moment(
                                    item.subscription_end_date,
                                    "DD/MM/YYYY",
                                  ).format("DD MMM YYYY")
                                ) : (
                                  <span
                                    style={{
                                      color: "#9ca3af",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    N/A
                                  </span>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                }}
                              >
                                {item.is_active === 1 ? (
                                  <span
                                    style={{
                                      background: "#f0fdf4",
                                      color: "#16a34a",
                                      border: "1px solid #bbf7d0",
                                      borderRadius: "20px",
                                      padding: "3px 12px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      display: "inline-block",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    ● Active
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      background: "#fef2f2",
                                      color: "#dc2626",
                                      border: "1px solid #fecaca",
                                      borderRadius: "20px",
                                      padding: "3px 12px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      display: "inline-block",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    ● Inactive
                                  </span>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Tooltip title="View Profile">
                                    <Link
                                      to={`/cms/manageUserData/user-profile/${item.user_id}`}
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#f0fdf4",
                                        color: "#16a34a",
                                        borderRadius: "8px",
                                        border: "1px solid #bbf7d0",
                                        textDecoration: "none",
                                        fontSize: "13px",
                                      }}
                                    >
                                      <EyeOutlined />
                                    </Link>
                                  </Tooltip>
                                  <Tooltip title="View Subscriptions">
                                    <Link
                                      to={`/cms/manage-customer/manage-order?userId=${item.user_id}&userName=${encodeURIComponent(item.customer_name || "")}`}
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#f0f4ff",
                                        color: "#667eea",
                                        borderRadius: "8px",
                                        border: "1px solid #c7d2fe",
                                        textDecoration: "none",
                                        fontSize: "13px",
                                      }}
                                    >
                                      <ShoppingOutlined />
                                    </Link>
                                  </Tooltip>
                                  {canDelete && (
                                    <Tooltip title="Delete">
                                      <button
                                        onClick={() =>
                                          handleDelete(item.user_id)
                                        }
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          background: "#fef2f2",
                                          color: "#dc2626",
                                          borderRadius: "8px",
                                          border: "1px solid #fecaca",
                                          cursor: "pointer",
                                          fontSize: "13px",
                                        }}
                                      >
                                        <DeleteOutlined />
                                      </button>
                                    </Tooltip>
                                  )}
                                  <Tooltip
                                    title={
                                      item.is_active === 1
                                        ? "Deactivate"
                                        : "Activate"
                                    }
                                  >
                                    <button
                                      onClick={() =>
                                        handleActive(
                                          item.is_active,
                                          item.user_id,
                                        )
                                      }
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#fffbeb",
                                        color: "#d97706",
                                        borderRadius: "8px",
                                        border: "1px solid #fde68a",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                      }}
                                    >
                                      <StopOutlined />
                                    </button>
                                  </Tooltip>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="10"
                              style={{
                                padding: "60px 20px",
                                textAlign: "center",
                              }}
                            >
                              <div style={{ color: "#9ca3af" }}>
                                <TeamOutlined
                                  style={{
                                    fontSize: "48px",
                                    marginBottom: "12px",
                                    display: "block",
                                  }}
                                />
                                <div
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                  }}
                                >
                                  No Data Found
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "#9ca3af",
                                    marginTop: "4px",
                                  }}
                                >
                                  Try adjusting your filters
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {/* Loading more indicator */}
                    {isLoadingMore && <LoadingMoreRow />}
                    {!hasMore &&
                      (listPageData || []).length > 0 &&
                      !isLoading && (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "14px",
                            fontSize: 12,
                            color: "#9ca3af",
                          }}
                        >
                          All{" "}
                          {kanbanFilter
                            ? (displayData || []).length
                            : totalUser}{" "}
                          records loaded
                        </div>
                      )}
                  </div>
                </>
              ) : (
                /* ═══ KANBAN VIEW ═══ */
                <div style={{ padding: "20px 24px" }}>
                  {isLoading || isKanbanLoading ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: 48,
                        color: "#94a3b8",
                        fontSize: 15,
                      }}
                    >
                      Loading...
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        gap: 16,
                        alignItems: "flex-start",
                        overflowX: "auto",
                        paddingBottom: 8,
                      }}
                    >
                      {[
                        {
                          key: "attorney",
                          label: "Attorney/IP Professional",
                          color: "#7c3aed",
                          headerBg: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
                          border: "#ede9fe",
                          items: (kanbanAllData || []).filter(
                            (u) => u.is_attorney === 1,
                          ),
                        },
                        {
                          key: "corporate",
                          label: "Corporate",
                          color: "#0891b2",
                          headerBg: "linear-gradient(135deg,#06b6d4,#0891b2)",
                          border: "#a5f3fc",
                          items: (kanbanAllData || []).filter(
                            (u) => u.is_attorney === 0,
                          ),
                        },
                      ].map((col) => (
                        <div
                          key={col.key}
                          style={{
                            flex: "0 0 calc(50% - 8px)",
                            minWidth: 290,
                            background: "#f8fafc",
                            borderRadius: 14,
                            border: `1px solid ${col.border}`,
                            overflow: "hidden",
                          }}
                        >
                          {/* Column header with View All */}
                          <div
                            style={{
                              background: col.headerBg,
                              padding: "12px 16px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  color: "#fff",
                                  fontWeight: 700,
                                  fontSize: 14,
                                }}
                              >
                                {col.label}
                              </span>
                              <span
                                style={{
                                  background: "rgba(255,255,255,0.25)",
                                  color: "#fff",
                                  borderRadius: 20,
                                  padding: "2px 10px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                {col.items.length}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                setKanbanFilter(col.key);
                                setViewMode("list");
                              }}
                              style={{
                                background: "rgba(255,255,255,0.2)",
                                border: "1px solid rgba(255,255,255,0.3)",
                                color: "#fff",
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "4px 12px",
                                borderRadius: 8,
                                transition: "all 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(255,255,255,0.35)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(255,255,255,0.2)";
                              }}
                            >
                              View All <BarsOutlined />
                            </button>
                          </div>

                          {/* Column cards */}
                          <div
                            style={{
                              padding: "10px",
                              maxHeight: 560,
                              overflowY: "auto",
                              display: "flex",
                              flexDirection: "column",
                              gap: 10,
                            }}
                          >
                            {col.items.length === 0 ? (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "32px 16px",
                                  color: "#9ca3af",
                                  fontSize: 13,
                                }}
                              >
                                No users
                              </div>
                            ) : (
                              col.items.map((item) => (
                                <div
                                  key={item.user_id}
                                  style={{
                                    background: "#fff",
                                    borderRadius: 10,
                                    padding: 14,
                                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                                    border: "1px solid #f1f5f9",
                                    borderLeft: `3px solid ${col.color}`,
                                    transition: "box-shadow 0.15s",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.boxShadow =
                                      "0 4px 12px rgba(0,0,0,0.1)")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.boxShadow =
                                      "0 1px 4px rgba(0,0,0,0.06)")
                                  }
                                >
                                  {/* Avatar + name + status */}
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                      marginBottom: 10,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "50%",
                                        background:
                                          "linear-gradient(135deg,#667eea,#764ba2)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {item.customer_name
                                        ?.split(" ")
                                        .map((w) => w.charAt(0))
                                        .join("")
                                        .slice(0, 2)
                                        .toUpperCase() || "?"}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <Link
                                        to={`/cms/manageUserData/user-360-view/${item.user_id}`}
                                        state={{ userName: item.customer_name }}
                                        style={{
                                          fontWeight: 700,
                                          color: "#1f2937",
                                          fontSize: 13,
                                          textDecoration: "none",
                                          display: "block",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {item.customer_name}
                                      </Link>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: "#6b7280",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {item.customer_email}
                                      </div>
                                    </div>
                                    <span
                                      style={{
                                        background:
                                          item.is_active === 1
                                            ? "#f0fdf4"
                                            : "#fef2f2",
                                        color:
                                          item.is_active === 1
                                            ? "#16a34a"
                                            : "#dc2626",
                                        borderRadius: 20,
                                        padding: "2px 8px",
                                        fontSize: 10,
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {item.is_active === 1
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  </div>
                                  {/* Badges */}
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 4,
                                      marginBottom: 10,
                                    }}
                                  >
                                    {item.plan_name && (
                                      <span
                                        style={{
                                          background: "#f0f4ff",
                                          color: "#667eea",
                                          borderRadius: 5,
                                          padding: "2px 7px",
                                          fontSize: 11,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {item.plan_name}
                                      </span>
                                    )}
                                    <span
                                      style={{
                                        background: "#fff7e6",
                                        color: "#d97706",
                                        borderRadius: 5,
                                        padding: "2px 7px",
                                        fontSize: 11,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {item.is_attorney === 1
                                        ? "Attorney"
                                        : "Corporate"}
                                    </span>
                                    {item.mobile && (
                                      <span
                                        style={{
                                          background: "#f1f5f9",
                                          color: "#475569",
                                          borderRadius: 5,
                                          padding: "2px 7px",
                                          fontSize: 11,
                                        }}
                                      >
                                        {withCountryCode(item.country_code, item.mobile)}
                                      </span>
                                    )}
                                  </div>
                                  {/* Dates */}
                                  {(item.subscription_start_date ||
                                    item.subscription_end_date) && (
                                    <div
                                      style={{
                                        fontSize: 11,
                                        color: "#6b7280",
                                        marginBottom: 10,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 3,
                                      }}
                                    >
                                      {item.subscription_start_date && (
                                        <div>
                                          <span style={{ color: "#9ca3af" }}>
                                            Subscription Start:
                                          </span>{" "}
                                          <b style={{ color: "#374151" }}>
                                            {moment(
                                              item.subscription_start_date,
                                              "DD/MM/YYYY",
                                            ).format("DD MMM YYYY")}
                                          </b>
                                        </div>
                                      )}
                                      {item.subscription_end_date && (
                                        <div>
                                          <span style={{ color: "#9ca3af" }}>
                                            Subscription End:
                                          </span>{" "}
                                          <b style={{ color: "#374151" }}>
                                            {moment(
                                              item.subscription_end_date,
                                              "DD/MM/YYYY",
                                            ).format("DD MMM YYYY")}
                                          </b>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {/* Actions */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 6,
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <Tooltip title="View Profile">
                                      <Link
                                        to={`/cms/manageUserData/user-profile/${item.user_id}`}
                                        style={{
                                          width: 28,
                                          height: 28,
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          background: "#f0fdf4",
                                          color: "#16a34a",
                                          borderRadius: 7,
                                          border: "1px solid #bbf7d0",
                                          textDecoration: "none",
                                          fontSize: 12,
                                        }}
                                      >
                                        <EyeOutlined />
                                      </Link>
                                    </Tooltip>
                                    <Tooltip title="View Subscriptions">
                                      <Link
                                        to={`/cms/manage-customer/manage-order?userId=${item.user_id}&userName=${encodeURIComponent(item.customer_name || "")}`}
                                        style={{
                                          width: 28,
                                          height: 28,
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          background: "#f0f4ff",
                                          color: "#667eea",
                                          borderRadius: 7,
                                          border: "1px solid #c7d2fe",
                                          textDecoration: "none",
                                          fontSize: 12,
                                        }}
                                      >
                                        <ShoppingOutlined />
                                      </Link>
                                    </Tooltip>
                                    {canDelete && (
                                      <Tooltip title="Delete">
                                        <button
                                          onClick={() =>
                                            handleDelete(item.user_id)
                                          }
                                          style={{
                                            width: 28,
                                            height: 28,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#fef2f2",
                                            color: "#dc2626",
                                            borderRadius: 7,
                                            border: "1px solid #fecaca",
                                            cursor: "pointer",
                                            fontSize: 12,
                                          }}
                                        >
                                          <DeleteOutlined />
                                        </button>
                                      </Tooltip>
                                    )}
                                    <Tooltip
                                      title={
                                        item.is_active === 1
                                          ? "Deactivate"
                                          : "Activate"
                                      }
                                    >
                                      <button
                                        onClick={() =>
                                          handleActive(
                                            item.is_active,
                                            item.user_id,
                                          )
                                        }
                                        style={{
                                          width: 28,
                                          height: 28,
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          background: "#fffbeb",
                                          color: "#d97706",
                                          borderRadius: 7,
                                          border: "1px solid #fde68a",
                                          cursor: "pointer",
                                          fontSize: 12,
                                        }}
                                      >
                                        <StopOutlined />
                                      </button>
                                    </Tooltip>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageUserData;
